import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  FormProvider,
  onlyDateToServer,
  Preloader,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import { toString } from 'lodash';

import { BackButton, Modal } from '@/components';
import { useSnackbar } from '@/modules/core/hooks';
import { CreatedPatientDto, CreatePatientDto } from '@/modules/UseMyMoney/queries';
import useAddFamilyMemberForm, { AddFamilyMemberForm, AddFamilyMemberFormField } from '@/modules/UseMyMoney/widgets/FieldSetup/hooks/useAddFamilyMemberForm';

import AddFamilyMemberModalForm from './AddFamilyMemberModalForm';

interface Props {
  onSetVisible: (value: boolean) => void;
  createPatient: (createPatientDto: CreatePatientDto) => Promise<CreatedPatientDto>;
  handleChangePatient: (patientId?: string) => void;
  providerFullName?: string;
  setProviderFullName?: (value: string) => void;
}

const AddFamilyMemberModal: React.FC<Props> = ({
  onSetVisible,
  createPatient,
  handleChangePatient,
  providerFullName,
  setProviderFullName,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();
  const { methods } = useAddFamilyMemberForm({
    defaultValues: {
      firstName: providerFullName?.split(' ')?.[0] || '',
      lastName: providerFullName?.split(' ')?.[1] || '',
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const { handleAddPermanentSnackbar } = useSnackbar();

  const handleCloseModal = useCallback(() => {
    onSetVisible(false);
    setProviderFullName?.('');
  }, [onSetVisible, setProviderFullName]);

  const handleCreatePatient = useCallback(async (formValues: AddFamilyMemberForm) => {
    try {
      setIsLoading(true);
      const createdPatient = await createPatient({
        first_name : formValues[AddFamilyMemberFormField.firstName],
        last_name : formValues[AddFamilyMemberFormField.lastName],
        ssn: formValues[AddFamilyMemberFormField.ssn],
        relationship_type: formValues[AddFamilyMemberFormField.relationship],
        birth_on: onlyDateToServer(formValues[AddFamilyMemberFormField.dateOfBirth]) as unknown as Date,
      });
      if (!createdPatient?.dependent_id) {
        return;
      }
      handleChangePatient(toString(createdPatient?.dependent_id));
      setIsLoading(false);
      onSetVisible(false);
      handleAddPermanentSnackbar({
        text: `${createdPatient?.first_name} ${createdPatient?.last_name} added.`,
        closeIcon: true,
        autoRemove: true,
      });
    } catch {
      setIsLoading(false);
    }
  }, [createPatient, handleChangePatient, handleAddPermanentSnackbar, onSetVisible]);

  return (
    <Modal
      testId="add-family-member"
      visible
      onSetVisible={handleCloseModal}
      isCloseButtonHidden={isMobile}
      isFullScreen={isMobile}
    >
      {isMobile ? (
        <Box>
          <BackButton
            testId="add-family-member-popup"
            onClick={handleCloseModal}
            title={t('Add New Family Member')}
          />
        </Box>
      ) : (
        <Text size="2xl" textAlign="center" weight={700}>
          {t('Add New Family Member')}
        </Text>
      )}

      <Box
        data-testId="add-family-member-form-wrapper_modal"
        width={{ max: 'none' }}
        margin={{ top: isMobile ? 'spacing16' : 'spacing24', horizontal: isMobile ? '-14px' : undefined }}
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
        round="moduleRound"
      >
        <FormProvider {...methods}>
          <AddFamilyMemberModalForm />
        </FormProvider>
      </Box>

      <Box
        margin={{ top: isMobile ? 'auto' : 'spacing24' }}
        direction="row"
        justify="end"
        gap="spacing12"
      >
        <Box flex={isMobile}>
          <AppButton
            testId="add-family-member-modal_cancel"
            onClick={handleCloseModal}
            buttonType="secondary"
            width={isMobile ? '100%' : '160px'}
          >
            {t('Cancel')}
          </AppButton>
        </Box>
        <Box flex={isMobile}>
          <AppButton
            testId="add-family-member-modal_submit"
            onClick={methods.handleSubmit(handleCreatePatient)}
            width={isMobile ? '100%' : '160px'}
            disabled={isLoading}
          >
            {isLoading ? <Preloader color="white" /> : t('Save')}
          </AppButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddFamilyMemberModal;
